/**
 * Shopify Storefront API Cart Integration
 * 
 * This file provides functions to integrate the Product Builder with Shopify cart.
 * 
 * SETUP:
 * 1. Get your Storefront API access token from Shopify Admin
 * 2. Set SHOPIFY_STORE_DOMAIN and SHOPIFY_STOREFRONT_TOKEN
 * 3. Create GraphQL mutation for cart operations
 */

// Configuration - Use shopify-config.js for configuration
// Get values from window.SHOPIFY_CONFIG (set by shopify-config.js)
// Use helper functions to avoid variable declaration conflicts

function getStoreDomain() {
    return (window.SHOPIFY_CONFIG && window.SHOPIFY_CONFIG.storeDomain) || 'your-store.myshopify.com';
}

function getStorefrontToken() {
    return (window.SHOPIFY_CONFIG && window.SHOPIFY_CONFIG.storefrontToken) || '';
}

function getApiVersion() {
    return (window.SHOPIFY_CONFIG && window.SHOPIFY_CONFIG.apiVersion) || '2024-01';
}

// GraphQL endpoint (constructed when needed)
function getGraphQLUrl() {
    return `https://${getStoreDomain()}/api/${getApiVersion()}/graphql.json`;
}

/**
 * Get or create a cart
 */
async function getOrCreateCart() {
    // Check for existing cart ID in localStorage
    let cartId = localStorage.getItem('shopify_cart_id');
    
    if (cartId) {
        try {
            // Verify cart still exists
            const cart = await fetchCart(cartId);
            if (cart) return cart;
        } catch (error) {
            console.warn('Existing cart not found, creating new one');
            cartId = null;
        }
    }
    
    // Create new cart
    const mutation = `
        mutation cartCreate {
            cartCreate {
                cart {
                    id
                    checkoutUrl
                }
            }
        }
    `;
    
    const response = await fetch(getGraphQLUrl(), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': getStorefrontToken()
        },
        body: JSON.stringify({ query: mutation })
    });
    
    const data = await response.json();
    if (data.errors) {
        throw new Error('Failed to create cart: ' + JSON.stringify(data.errors));
    }
    
    const newCartId = data.data.cartCreate.cart.id;
    localStorage.setItem('shopify_cart_id', newCartId);
    return data.data.cartCreate.cart;
}

/**
 * Fetch cart by ID
 */
async function fetchCart(cartId) {
    const query = `
        query getCart($cartId: ID!) {
            cart(id: $cartId) {
                id
                checkoutUrl
                lines(first: 100) {
                    edges {
                        node {
                            id
                            quantity
                            merchandise {
                                ... on ProductVariant {
                                    id
                                    title
                                }
                            }
                        }
                    }
                }
            }
        }
    `;
    
    const response = await fetch(getGraphQLUrl(), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': getStorefrontToken()
        },
        body: JSON.stringify({
            query: query,
            variables: { cartId: cartId }
        })
    });
    
    const data = await response.json();
    if (data.errors) {
        throw new Error('Failed to fetch cart: ' + JSON.stringify(data.errors));
    }
    
    return data.data.cart;
}

/**
 * Add item to cart with custom attributes
 * 
 * @param {string} variantId - Shopify product variant ID (gid://shopify/ProductVariant/xxxxx)
 * @param {number} quantity - Quantity to add
 * @param {Object} attributes - Custom attributes (product configuration)
 * @param {string} attributes.productType - Product type (driver, fairway, etc.)
 * @param {string} attributes.materialColor - Selected material color
 * @param {string} attributes.furColor - Selected fur color
 * @param {boolean} attributes.hasLogo - Whether logo was added
 * @param {Array} attributes.texts - Array of text customizations
 */
async function addToShopifyCart(variantId, quantity = 1, attributes = {}) {
    try {
        // Get or create cart
        const cart = await getOrCreateCart();
        const cartId = cart.id;
        
        // Format attributes for Shopify line item properties (values must be strings).
        // Objects/arrays (e.g. _texts) must be JSON.stringify'd — String([{...}]) becomes "[object Object]".
        function toCartAttributeValue(value) {
            if (value === null || value === undefined) return '';
            if (typeof value === 'string') return value;
            if (typeof value === 'number' || typeof value === 'boolean') return String(value);
            try {
                return JSON.stringify(value);
            } catch (e) {
                return String(value);
            }
        }
        const attributeEntries = Object.entries(attributes).map(([key, value]) => ({
            key: key,
            value: toCartAttributeValue(value)
        }));
        // Do NOT add JSON.stringify(attributes) as _builder_config: it duplicates every property
        // (including large data: URLs) and triggers Shopify "Cart is too large" / 422 errors.

        // GraphQL mutation to add item
        const mutation = `
            mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
                cartLinesAdd(cartId: $cartId, lines: $lines) {
                    cart {
                        id
                        checkoutUrl
                        lines(first: 100) {
                            edges {
                                node {
                                    id
                                    quantity
                                    merchandise {
                                        ... on ProductVariant {
                                            id
                                            title
                                        }
                                    }
                                }
                            }
                        }
                    }
                    userErrors {
                        field
                        message
                    }
                }
            }
        `;
        
        const variables = {
            cartId: cartId,
            lines: [{
                merchandiseId: variantId,
                quantity: quantity,
                attributes: attributeEntries
            }]
        };
        
        const response = await fetch(getGraphQLUrl(), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Shopify-Storefront-Access-Token': getStorefrontToken()
            },
            body: JSON.stringify({
                query: mutation,
                variables: variables
            })
        });
        
        const data = await response.json();
        
        if (data.errors) {
            throw new Error('GraphQL errors: ' + JSON.stringify(data.errors));
        }
        
        if (data.data.cartLinesAdd.userErrors && data.data.cartLinesAdd.userErrors.length > 0) {
            throw new Error('Cart errors: ' + JSON.stringify(data.data.cartLinesAdd.userErrors));
        }
        
        const updatedCart = data.data.cartLinesAdd.cart;
        return {
            success: true,
            cartId: updatedCart.id,
            checkoutUrl: updatedCart.checkoutUrl
        };
        
    } catch (error) {
        console.error('Error adding to Shopify cart:', error);
        throw error;
    }
}

/**
 * Redirect to Shopify checkout
 */
function goToShopifyCheckout(checkoutUrl) {
    if (checkoutUrl) {
        window.location.href = checkoutUrl;
    } else {
        console.error('No checkout URL available');
    }
}

// Export functions for use in index.html
if (typeof window !== 'undefined') {
    window.shopifyCart = {
        addToCart: addToShopifyCart,
        getOrCreateCart: getOrCreateCart,
        goToCheckout: goToShopifyCheckout
    };
}


